import {
  ApplicantStatus,
  ExecutionModes,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { AlertCircle, InfoCircle, Loading02, Minus } from "untitledui-js-base";
import { useQuery } from "@tanstack/react-query";
import { CreditReport, VerificationReport } from "types/landlord/reports";
import api from "api/landlord/applications";
import {
  errorHitCodes,
  isNoHitCode
} from "views/applications-id-report/slices/CreditReportSlice/utils";
import { cx } from "utils/classnames";

const LoadingIcon = ({ isSelected }: { isSelected: boolean }) => (
  <Loading02
    className={cx(
      "stroke-content-100 w-4 h-4",
      isSelected ? "stroke-primary-500" : "stroke-content-100"
    )}
  />
);

const getStatusBasedIcon = (
  verificationStatus: VerificationStatus,
  isSelected: boolean
) => {
  switch (verificationStatus) {
    case VerificationStatus.Failed:
      return <AlertCircle className="stroke-danger-500 w-4 h-4" />;
    case VerificationStatus.FinishedWithWarnings:
      return <AlertCircle className="stroke-warning-600 w-4 h-4" />;
    case VerificationStatus.Processing:
    case VerificationStatus.Started:
    case VerificationStatus.Submitted:
      return <LoadingIcon isSelected={isSelected} />;
    case VerificationStatus.Pending:
    case VerificationStatus.Skipped:
      return <Minus className="stroke-content-100 w-4 h-4" />;
  }
};

/*
 * Cases that render a neutral dash: a report held back by an identity or warnings
 * report override, and pet screening whose invite has not gone out. Kept out of the
 * component both because it is pure and to keep StatusIcon under the
 * cognitive-complexity limit.
 */
const showsNeutralDash = ({
  isAwaitingRequest,
  isIdentityVerificationOverrideRequired,
  isWarningsReportOverrideRequired,
  verificationStatus,
  name
}: {
  isAwaitingRequest: boolean;
  isIdentityVerificationOverrideRequired: boolean;
  isWarningsReportOverrideRequired: boolean;
  verificationStatus: VerificationStatus;
  name: VerificationNames;
}) =>
  isAwaitingRequest ||
  ((isIdentityVerificationOverrideRequired ||
    // Matches the report panel: only reports that have not run yet are paused.
    (isWarningsReportOverrideRequired &&
      verificationStatus === VerificationStatus.Pending)) &&
    [
      VerificationNames.CreditReport,
      VerificationNames.CriminalReport,
      VerificationNames.EvictionReport
    ].includes(name));

const getIdentityVerificationIcon = (
  verificationStatus: VerificationStatus
) => {
  switch (verificationStatus) {
    case VerificationStatus.FinishedWithWarnings:
      return <AlertCircle className="stroke-warning-600" size="16px" />;
    case VerificationStatus.Failed:
      return <AlertCircle className="stroke-danger-500" size="16px" />;
  }
};

interface StatusIconProps {
  applicantStatus?: ApplicantStatus;
  verificationStatus: VerificationStatus;
  name: VerificationNames;
  executionMode?: ExecutionModes;
  isManuallyOverridden: boolean;
  isIdentityVerificationOverrideRequired: boolean;
  isWarningsReportOverrideRequired?: boolean;
  applicationId: number;
  applicantId: number;
  verificationId: number;
  isSelected: boolean;
  isRerun: boolean;
  isAwaitingForCounty?: boolean;
  /*
   * Pet screening whose invite has not gone out (and, on a declined application,
   * never will). Nothing is in flight, so the tab must not spin.
   */
  isAwaitingRequest?: boolean;
  isSsnMismatched?: boolean;
  hasWarnings?: boolean;
  hasActiveReverifyRequest?: boolean;
  hasProcessingIncome?: boolean;
}

export const StatusIcon = ({
  applicantStatus,
  verificationStatus,
  name,
  executionMode,
  isManuallyOverridden,
  isIdentityVerificationOverrideRequired,
  isWarningsReportOverrideRequired = false,
  applicationId,
  applicantId,
  verificationId,
  isSelected,
  isRerun,
  isAwaitingForCounty,
  isAwaitingRequest = false,
  isSsnMismatched,
  hasWarnings = false,
  hasActiveReverifyRequest = false,
  hasProcessingIncome = false
}: StatusIconProps) => {
  const { data: creditReport } = useQuery<VerificationReport<CreditReport>>({
    queryKey: ["CREDIT_REPORT", applicationId, verificationId, applicantId],
    queryFn: () =>
      api
        .getApplicantVerification(applicationId, applicantId, verificationId)
        .then((res) => res.verification),
    enabled: name === VerificationNames.CreditReport
  });

  if (
    isAwaitingForCounty ||
    (name === VerificationNames.CombinedIncome &&
      (hasActiveReverifyRequest || hasProcessingIncome))
  )
    return <Loading02 className="w-4 h-4 stroke-warning-500" />;

  if (isManuallyOverridden) return;

  if (
    showsNeutralDash({
      isAwaitingRequest,
      isIdentityVerificationOverrideRequired,
      isWarningsReportOverrideRequired,
      verificationStatus,
      name
    })
  )
    return <Minus className="stroke-content-100 w-4 h-4" />;

  if (
    (isRerun || applicantStatus === ApplicantStatus.Submitted) &&
    verificationStatus === VerificationStatus.Pending
  )
    return <LoadingIcon isSelected={isSelected} />;

  if (
    (verificationStatus === VerificationStatus.Pending &&
      executionMode === ExecutionModes.Manual &&
      applicantStatus === ApplicantStatus.Submitted) ||
    errorHitCodes.includes(creditReport?.report.hit_code?.code || "")
  )
    return <AlertCircle className="stroke-danger-500" size="16px" />;

  /*
   * A no-hit is informational, not a warning: the bureau simply has no credit
   * file for this applicant, so we show a neutral info icon instead of the
   * amber warning that "finished_with_warnings" would otherwise produce.
   */
  if (
    name === VerificationNames.CreditReport &&
    isNoHitCode(creditReport?.report?.hit_code)
  )
    return <InfoCircle className="stroke-content-200" size="16px" />;

  if (name === VerificationNames.IdentityVerification) {
    const idvIcon = getIdentityVerificationIcon(verificationStatus);
    if (idvIcon) return idvIcon;
  }

  return getWarningsOrStatusIcon({
    verificationStatus,
    isSelected,
    hasWarnings: Boolean(hasWarnings || isSsnMismatched)
  });
};

// A failed verification (e.g. a failed premium warnings report) must show the
// red failed icon; the amber warnings tint only applies to settled ones.
const getWarningsOrStatusIcon = ({
  verificationStatus,
  isSelected,
  hasWarnings
}: {
  verificationStatus: VerificationStatus;
  isSelected: boolean;
  hasWarnings: boolean;
}) => {
  if (hasWarnings && verificationStatus !== VerificationStatus.Failed)
    return <AlertCircle className="stroke-warning-600 w-4 h-4" />;

  return getStatusBasedIcon(verificationStatus, isSelected);
};
